import { Check, Clear, Comment, Delete, Edit } from '@mui/icons-material'
import { Box, Button, ClickAwayListener, Popover, TableCell, TextField, Tooltip, Typography } from '@mui/material'
import { Dayjs } from 'dayjs'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import {
  WorkforceScheduleModify,
  useCreateWorkforceScheduleMutation,
} from '../../store/redux/reducers/workforceScheduleSlice'
import { Project, User, WorkforceSchedule } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import { SquareButton } from '../../styles/ButtonStyles'
import { createErrorMessage } from '../../store/utils'

const EditableParticipationTableCell = ({
  isEditMode,
  isEditionDisabled,
  workforceData,
  currentEmployee,
  project,
  date,
}: {
  isEditMode: boolean
  isEditionDisabled: boolean
  workforceData: WorkforceSchedule | undefined
  currentEmployee: User
  project: Project
  date: Dayjs
}) => {
  const { t } = useTranslation()
  const { leaderId } = useAppSelector(state => state.leaderId)
  const [anchorEl, setAnchorEl] = useState<HTMLTableCellElement | null>(null)
  const [isDescriptionEditable, setIsDescriptionEditable] = useState(false)
  const [isCellEditable, setIsCellEditable] = useState(false)
  const [cellPercent, setCellPercent] = useState(0)
  const [cellDescriptionValue, setCellDescriptionValue] = useState('')
  const [description, setDescription] = useState('')
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined
  const { displayNotification } = useNotification()

  const [createWorkforce] = useCreateWorkforceScheduleMutation()

  useEffect(() => {
    setCellPercent(workforceData?.percent || 0)
    setCellDescriptionValue(workforceData?.description || '')
  }, [workforceData])

  useEffect(() => {
    setDescription(cellDescriptionValue)
  }, [cellDescriptionValue])

  const handleParticipationInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value

    if (value !== '') {
      value = String(Math.min(Math.max(parseInt(value) || 0, 0), 100))
    }

    event.target.value = value
    const cellStringValue = value !== '' ? value : '0'
    setCellPercent(parseInt(cellStringValue))
  }

  const handleClose = () => {
    setAnchorEl(null)
    handleDescriptionCancelClick()
  }

  useEffect(() => {
    setIsCellEditable(false)
  }, [isEditMode])

  const handleDescriptionEditClick = () => {
    setIsDescriptionEditable(true)
  }

  const handleDescriptionCancelClick = () => {
    setIsDescriptionEditable(false)
    setDescription(cellDescriptionValue)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDescription(e.target.value)
  }

  const handleDescriptionDeleteClick = async () => {
    if (cellDescriptionValue === '' || !workforceData) {
      return
    }

    const updatedWorkforceData: WorkforceScheduleModify = {
      ...workforceData,
      projectId: workforceData.project.id,
      employeeId: workforceData.employee.id,
      date: date.format(DateFormat.YearMonth),
      description: undefined,
      removed: false,
    }

    try {
      await createWorkforce({ leaderId: leaderId!!, workforceSchedules: [updatedWorkforceData] }).unwrap()
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleDescriptionSaveClick = async () => {
    setIsDescriptionEditable(false)

    if (description === cellDescriptionValue) {
      return
    }

    const updatedWorkforceData: WorkforceScheduleModify = workforceData
      ? {
          ...workforceData,
          projectId: workforceData.project.id,
          employeeId: workforceData.employee.id,
          description: description,
          date: date.format(DateFormat.YearMonth),
          removed: false,
        }
      : {
          employeeId: currentEmployee.id,
          description: description,
          percent: cellPercent,
          date: date.format(DateFormat.YearMonth),
          projectId: project.id,
          removed: false,
        }

    try {
      await createWorkforce({ leaderId: leaderId!!, workforceSchedules: [updatedWorkforceData] }).unwrap()
      setCellDescriptionValue(description)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleCellDoubleClick = (
    event: React.MouseEvent<HTMLTableCellElement> | React.KeyboardEvent<HTMLTableCellElement>
  ) => {
    if (isEditionDisabled) return
    isEditMode ? setIsCellEditable(true) : setAnchorEl(event.currentTarget)
  }

  const handleSave = async () => {
    setIsCellEditable(false)

    if (isNaN(cellPercent)) {
      setCellPercent(0)
    }

    if (cellPercent === workforceData?.percent) {
      return
    }

    const updatedWorkforceData: WorkforceScheduleModify = workforceData
      ? {
          ...workforceData,
          employeeId: workforceData.employee.id,
          projectId: workforceData.project.id,
          date: date.format(DateFormat.YearMonth),
          percent: cellPercent,
          removed: false,
        }
      : {
          employeeId: currentEmployee.id,
          description: description,
          percent: cellPercent,
          date: date.format(DateFormat.YearMonth),
          projectId: project.id,
          removed: false,
        }

    try {
      await createWorkforce({ leaderId: leaderId!!, workforceSchedules: [updatedWorkforceData] }).unwrap()
      setCellPercent(cellPercent)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorDescription}`, type: 'error' })
    }
  }

  const onTextFieldEnterClick = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
  }

  const onCellClickAway = () => {
    if (isCellEditable) {
      handleSave()
    }
  }

  return (
    <>
      <ClickAwayListener onClickAway={onCellClickAway}>
        <TableCell
          onClick={handleCellDoubleClick}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleCellDoubleClick(event)
            }
          }}
          sx={{
            transition: 'all 150ms ease-in-out',
            backgroundColor: 'background.main',
            ':hover': {
              backgroundColor: isEditionDisabled ? 'primary.transparentHover' : 'background.elevation2',
              borderRadius: '32px',
              cursor: isEditionDisabled ? 'default' : 'pointer',
            },
            ':focus': {
              backgroundColor: isEditionDisabled ? 'primary.transparentHover' : 'background.elevation2',
              borderRadius: '32px',
              outline: 'none',
            },
            height: '50px',
          }}
          align="center"
          tabIndex={0}
        >
          {!isCellEditable ? (
            <Tooltip title={!isEditMode ? cellDescriptionValue : undefined}>
              <Box padding={'4px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                {cellDescriptionValue !== '' && !isEditMode && (
                  <Comment sx={{ color: 'text.lighter', width: '16px', height: '16px', mr: '8px' }} />
                )}
                {cellPercent === 0 ? '-' : cellPercent + '%'}
              </Box>
            </Tooltip>
          ) : (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <TextField
                onKeyDown={onTextFieldEnterClick}
                fullWidth
                value={cellPercent}
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    max: 100,
                    style: { textAlign: 'center', display: 'inline', width: 50 },
                  },
                }}
                sx={{ width: '70px' }}
                size="small"
                id="standard-basic"
                variant="outlined"
                onChange={handleParticipationInputChange}
              />
            </Box>
          )}
        </TableCell>
      </ClickAwayListener>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box display={'flex'} flexDirection={'column'} padding={'24px'} width={'524px'} gap={'10px'}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box display={'flex'} alignItems={'center'} gap={'8px'}>
              <Typography
                component="h3"
                variant="h5"
              >{`${currentEmployee.firstName} ${currentEmployee.lastName}`}</Typography>
              <Typography variant="body1">{project.name ?? project.acronym}</Typography>
            </Box>
            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
              {isDescriptionEditable ? (
                <>
                  <Button
                    disableElevation
                    onClick={handleDescriptionCancelClick}
                    variant="contained"
                    color="secondary"
                    startIcon={<Clear />}
                    size="small"
                  >
                    {t('page.projectDistribution.cancel')}
                  </Button>
                  <Button
                    disableElevation
                    onClick={handleDescriptionSaveClick}
                    variant="contained"
                    color="primary"
                    startIcon={<Check />}
                    size="small"
                  >
                    {t('page.projectDistribution.save')}
                  </Button>
                </>
              ) : (
                <>
                  <Tooltip title={t('page.projectDistribution.delete')}>
                    <SquareButton onClick={handleDescriptionDeleteClick} size="small" color="secondary">
                      <Delete sx={{ height: '18px', width: '18px' }} />
                    </SquareButton>
                  </Tooltip>
                  <Tooltip title={t('page.projectDistribution.edit')}>
                    <SquareButton onClick={handleDescriptionEditClick} color="primary" size="small">
                      <Edit sx={{ height: '18px', width: '18px' }} />
                    </SquareButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
          {isDescriptionEditable ? (
            <TextField
              multiline
              inputProps={{ maxLength: 500 }}
              onChange={handleDescriptionChange}
              value={description}
              label={t('page.projectDistribution.description')}
              size="small"
            />
          ) : (
            <Typography variant="body2" sx={{ overflowWrap: 'break-word' }}>
              {description}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default EditableParticipationTableCell